import { useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '../shared/api/client';
import {
  DELIVERY_MODES,
  api,
  type LocationView,
  type ZoneGeometry,
  type ZoneView,
} from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { useDepartments } from '../shared/lib/departments';
import { parseGeometryText, vertexCount } from '../shared/lib/geometry';
import {
  ZONE_NAME_MAX,
  emptyFactorRow,
  emptyZoneForm,
  hasZoneErrors,
  toZoneInput,
  validateZoneForm,
  zoneErrorsFromProblem,
  zoneFormFrom,
  type FactorRow,
  type ZoneField,
  type ZoneFormErrors,
  type ZoneFormState,
} from '../shared/lib/zone-form';
import {
  DISABLED_OPACITY,
  FieldNote,
  FormAlert,
  FormField,
  actionsStyle,
  checkStyle,
  checksStyle,
  describedBy,
  errorStyle,
  fieldsetStyle,
  formStyle,
  hintStyle,
  labelStyle,
  pairStyle,
  sectionTitleStyle,
  supportCode,
} from '../shared/ui/form';
import { ZoneEditorMap, type MapStatus } from '../shared/ui/MapView';
import {
  ConfirmDialog,
  Drawer,
  dangerButtonStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  smallButtonStyle,
} from '../shared/ui/primitives';

/**
 * Зона (разд. 12, FR-502, FR-503, A12): заведение и правка.
 *
 * Полигон рисуется на карте поверх соседних зон — зоны пересекаются, и при попадании
 * адреса в несколько применяется зона с наивысшим приоритетом (FR-503). Поэтому
 * приоритет — обязательное поле без умолчания, а не «0, если забыли».
 *
 * Без карты (ключ не задан или не разрешён для домена) полигон вставляется текстом
 * GeoJSON — из geojson.io или выгрузки ГИС. Этот путь открыт и при живой карте: зону
 * района проще вставить из готовой границы, чем обвести мышью.
 *
 * Отключение — `DELETE`, деактивация: заказы ссылаются на зону, и стирать её нельзя.
 */
export function ZoneDrawer({
  zone,
  zones,
  locations,
  onClose,
  onSaved,
}: {
  readonly zone?: ZoneView | undefined;
  /** Все зоны — для контекста на карте; редактируемая среди них отфильтровывается. */
  readonly zones: readonly ZoneView[];
  readonly locations: readonly LocationView[];
  readonly onClose: () => void;
  readonly onSaved: (zone: ZoneView | null, kind: 'created' | 'saved' | 'deactivated') => void;
}): ReactElement {
  const messages = useMessages();
  const text = messages.cabinet.zoneForm;
  const departments = useDepartments();
  const editing = zone !== undefined;

  const [form, setForm] = useState<ZoneFormState>(() =>
    zone === undefined ? emptyZoneForm(departments) : zoneFormFrom(zone),
  );
  const [errors, setErrors] = useState<ZoneFormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [general, setGeneral] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [drawRequest, setDrawRequest] = useState(0);
  const [mapStatus, setMapStatus] = useState<MapStatus>('loading');
  const [geoText, setGeoText] = useState('');
  const [geoTextError, setGeoTextError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const context = zones
    .filter((item) => item.id !== zone?.id && item.is_active !== false)
    .map((item) => ({ id: item.id, name: item.name, geometry: item.geometry }));
  const shownModes = [...new Set([...departments, form.mode])];
  const mapReady = mapStatus === 'ready';

  const update = (patch: Partial<ZoneFormState>): void => {
    setForm((current) => {
      const next = { ...current, ...patch };

      if (submitted) {
        setErrors(validateZoneForm(next));
      }

      return next;
    });
  };

  const setGeometry = (geometry: ZoneGeometry | null): void => update({ geometry });

  const setFactor = (index: number, patch: Partial<FactorRow>): void =>
    update({
      traffic_factors: form.traffic_factors.map((row, position) =>
        position === index ? { ...row, ...patch } : row,
      ),
    });

  const failure = (cause: unknown, fallback: string): string => {
    if (!(cause instanceof ApiError)) {
      return fallback;
    }

    if (cause.status === FORBIDDEN) {
      return text.errors.forbidden;
    }

    return `${cause.code === 'validation_failed' ? text.errors.validation : fallback} · ${messages.cabinet.drivers.errors.code}: ${supportCode(cause)}`;
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitted(true);
    setGeneral(null);

    const found = validateZoneForm(form);
    setErrors(found);

    if (hasZoneErrors(found)) {
      return;
    }

    setPending(true);

    try {
      if (zone === undefined) {
        onSaved(await api.createZone(toZoneInput(form)), 'created');
      } else {
        onSaved(await api.updateZone(zone.id, toZoneInput(form)), 'saved');
      }
    } catch (cause) {
      if (cause instanceof ApiError) {
        const mapped = zoneErrorsFromProblem(cause);
        setErrors(mapped.fields);

        if (mapped.general) {
          setGeneral(failure(cause, text.errors.failed));
        }
      } else {
        setGeneral(text.errors.failed);
      }
    } finally {
      setPending(false);
    }
  };

  const deactivate = async (): Promise<void> => {
    if (zone === undefined) {
      return;
    }

    setPending(true);
    setConfirmError(null);

    try {
      await api.deactivateZone(zone.id);
      setConfirming(false);
      onSaved(null, 'deactivated');
    } catch (cause) {
      setConfirmError(failure(cause, text.errors.deactivateFailed));
    } finally {
      setPending(false);
    }
  };

  const applyGeoText = (): void => {
    const parsed = parseGeometryText(geoText);

    setGeoTextError(parsed === null);

    if (parsed !== null) {
      setGeometry(parsed);
      setGeoText('');
    }
  };

  const errorOf = (field: ZoneField): string | null => {
    const code = errors[field];

    return code === undefined ? null : text.errors[code];
  };

  const vertices = vertexCount(form.geometry);
  const title = editing ? text.editTitle : text.createTitle;

  return (
    <Drawer
      title={<h2>{title}</h2>}
      label={title}
      onClose={onClose}
      trapFocus
      actions={
        editing && zone.is_active !== false ? (
          <button
            type="button"
            style={dangerButtonStyle}
            onClick={() => {
              setConfirmError(null);
              setConfirming(true);
            }}
          >
            {text.deactivate}
          </button>
        ) : null
      }
    >
      <form noValidate onSubmit={(event) => void submit(event)} style={formStyle}>
        <FormField
          id="zone-name"
          label={messages.cabinet.directories.name}
          required={text.required}
          error={errorOf('name')}
        >
          <input
            id="zone-name"
            value={form.name}
            maxLength={ZONE_NAME_MAX}
            autoComplete="off"
            aria-invalid={errors.name !== undefined}
            aria-describedby={describedBy('zone-name', errors.name !== undefined)}
            onChange={(event) => update({ name: event.target.value })}
          />
        </FormField>

        <div style={pairStyle}>
          <FormField id="zone-mode" label={messages.cabinet.order.mode} error={errorOf('mode')}>
            <select
              id="zone-mode"
              value={form.mode}
              aria-invalid={errors.mode !== undefined}
              aria-describedby={describedBy('zone-mode', errors.mode !== undefined)}
              onChange={(event) => update({ mode: event.target.value as ZoneFormState['mode'] })}
            >
              {DELIVERY_MODES.filter((mode) => shownModes.includes(mode)).map((mode) => (
                <option key={mode} value={mode} disabled={!departments.includes(mode)}>
                  {messages.cabinet.modes[mode]}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            id="zone-priority"
            label={text.priority}
            required={text.required}
            hint={text.priorityHint}
            error={errorOf('priority')}
          >
            <input
              id="zone-priority"
              className="num"
              inputMode="numeric"
              autoComplete="off"
              value={form.priority}
              aria-invalid={errors.priority !== undefined}
              aria-describedby={describedBy('zone-priority', errors.priority !== undefined, true)}
              onChange={(event) => update({ priority: event.target.value })}
            />
          </FormField>
        </div>

        <FormField
          id="zone-sla"
          label={text.sla}
          hint={text.slaHint}
          error={errorOf('sla_minutes')}
        >
          <input
            id="zone-sla"
            className="num"
            inputMode="numeric"
            autoComplete="off"
            value={form.sla_minutes}
            aria-invalid={errors.sla_minutes !== undefined}
            aria-describedby={describedBy('zone-sla', errors.sla_minutes !== undefined, true)}
            onChange={(event) => update({ sla_minutes: event.target.value })}
          />
        </FormField>

        <fieldset
          style={fieldsetStyle}
          aria-describedby={describedBy('zone-geometry', errors.geometry !== undefined, true)}
        >
          <legend style={sectionTitleStyle}>{`${text.geometry} · ${text.required}`}</legend>
          <ZoneEditorMap
            label={text.mapLabel}
            geometry={form.geometry}
            onChange={setGeometry}
            contextZones={context}
            drawRequest={drawRequest}
            onStatus={setMapStatus}
          />
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', alignItems: 'center' }}
          >
            {mapReady ? (
              <button
                type="button"
                style={smallButtonStyle}
                onClick={() => setDrawRequest((value) => value + 1)}
              >
                {form.geometry === null ? text.draw : text.redraw}
              </button>
            ) : null}
            {form.geometry === null ? null : (
              <button type="button" style={smallButtonStyle} onClick={() => setGeometry(null)}>
                {text.clearGeometry}
              </button>
            )}
            <span className="num" style={hintStyle}>
              {form.geometry === null
                ? text.noGeometry
                : form.geometry.type === 'MultiPolygon'
                  ? text.multiPolygon
                  : text.vertices.replace('{count}', String(vertices))}
            </span>
          </div>
          <FieldNote
            id="zone-geometry"
            hint={mapReady ? text.drawHint : undefined}
            error={errorOf('geometry')}
          />

          {/* Запасной путь без карты — и короткий путь для готовой границы района. */}
          <details open={!mapReady && mapStatus !== 'loading'}>
            <summary
              style={{
                cursor: 'pointer',
                minHeight: 'var(--field-h)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {text.pasteGeoJson}
            </summary>
            <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
              <label htmlFor="zone-geojson" style={labelStyle}>
                {text.geoJsonLabel}
              </label>
              <textarea
                id="zone-geojson"
                className="num"
                rows={GEOJSON_ROWS}
                value={geoText}
                spellCheck={false}
                aria-invalid={geoTextError}
                aria-describedby={geoTextError ? 'zone-geojson-error' : 'zone-geojson-hint'}
                onChange={(event) => {
                  setGeoText(event.target.value);
                  setGeoTextError(false);
                }}
                style={{ minWidth: 0, resize: 'vertical' }}
              />
              {geoTextError ? (
                <span id="zone-geojson-error" style={errorStyle}>
                  {text.errors.geoJsonInvalid}
                </span>
              ) : (
                <span id="zone-geojson-hint" style={hintStyle}>
                  {text.geoJsonHint}
                </span>
              )}
              <div>
                <button
                  type="button"
                  style={smallButtonStyle}
                  disabled={geoText.trim() === ''}
                  onClick={applyGeoText}
                >
                  {text.applyGeoJson}
                </button>
              </div>
            </div>
          </details>
        </fieldset>

        <fieldset
          style={fieldsetStyle}
          aria-describedby={describedBy('zone-factors', errors.traffic_factors !== undefined, true)}
        >
          <legend style={sectionTitleStyle}>{text.factors}</legend>
          {form.traffic_factors.map((row, index) => (
            <div key={index} style={factorRowStyle}>
              <input
                type="time"
                className="num"
                aria-label={`${text.factorRow.replace('{n}', String(index + 1))} · ${text.fromTime}`}
                value={row.from_time}
                style={{ minWidth: 0 }}
                onChange={(event) => setFactor(index, { from_time: event.target.value })}
              />
              <input
                type="time"
                className="num"
                aria-label={`${text.factorRow.replace('{n}', String(index + 1))} · ${text.toTime}`}
                value={row.to_time}
                style={{ minWidth: 0 }}
                onChange={(event) => setFactor(index, { to_time: event.target.value })}
              />
              <input
                className="num"
                inputMode="decimal"
                placeholder={text.factorPlaceholder}
                aria-label={`${text.factorRow.replace('{n}', String(index + 1))} · ${text.factor}`}
                value={row.factor}
                style={{ minWidth: 0 }}
                onChange={(event) => setFactor(index, { factor: event.target.value })}
              />
              <button
                type="button"
                style={{ ...smallButtonStyle, minHeight: 'var(--field-h)' }}
                aria-label={`${text.removeFactor} ${index + 1}`}
                onClick={() =>
                  update({
                    traffic_factors: form.traffic_factors.filter(
                      (_, position) => position !== index,
                    ),
                  })
                }
              >
                {text.remove}
              </button>
            </div>
          ))}
          <div>
            <button
              type="button"
              style={smallButtonStyle}
              onClick={() =>
                update({ traffic_factors: [...form.traffic_factors, emptyFactorRow()] })
              }
            >
              {text.addFactor}
            </button>
          </div>
          <FieldNote id="zone-factors" hint={text.factorsHint} error={errorOf('traffic_factors')} />
        </fieldset>

        <fieldset
          style={fieldsetStyle}
          aria-describedby={describedBy('zone-locations', errors.location_ids !== undefined, true)}
        >
          <legend style={labelStyle}>{text.locations}</legend>
          {locations.length === 0 ? (
            <span style={hintStyle}>{text.noLocations}</span>
          ) : (
            <div style={checksStyle}>
              {locations.map((location) => (
                <label key={location.id} style={checkStyle}>
                  <input
                    type="checkbox"
                    checked={form.location_ids.includes(location.id)}
                    onChange={() =>
                      update({
                        location_ids: form.location_ids.includes(location.id)
                          ? form.location_ids.filter((id) => id !== location.id)
                          : [...form.location_ids, location.id],
                      })
                    }
                  />
                  <span>{location.name}</span>
                </label>
              ))}
            </div>
          )}
          <FieldNote
            id="zone-locations"
            hint={text.locationsHint}
            error={errorOf('location_ids')}
          />
        </fieldset>

        <label style={checkStyle}>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(event) => update({ is_active: event.target.checked })}
          />
          <span>{text.active}</span>
        </label>

        <FormAlert text={general} />

        <div style={actionsStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            {messages.cabinet.common.cancel}
          </button>
          <button
            type="submit"
            disabled={pending}
            aria-busy={pending}
            style={{ ...primaryButtonStyle, opacity: pending ? DISABLED_OPACITY : 1 }}
          >
            {pending ? text.saving : editing ? text.save : text.submit}
          </button>
        </div>
      </form>

      {confirming && zone !== undefined ? (
        <ConfirmDialog
          title={text.deactivateTitle}
          description={text.deactivateDescription.replace('{name}', zone.name)}
          confirmLabel={text.deactivate}
          pending={pending}
          error={confirmError}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void deactivate()}
        />
      ) : null}
    </Drawer>
  );
}

const FORBIDDEN = 403;
const GEOJSON_ROWS = 4;

const factorRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.8fr) auto',
  gap: 'var(--sp-2)',
  alignItems: 'center',
} as const;
